/**
 * API Service - Google OAuth JWT Authentication
 * ==============================================
 * Axios client with JWT token management for Google OAuth.
 */

import axios from 'axios';

// Global variable to store auth context
let authContext = null;

// Function to set auth context (called by AuthProvider)
export const setAuthContext = (context) => {
  authContext = context;
};

// Storage key for token
const TOKEN_KEY = 'auth_token';

// Create axios instance with base configuration
const getBackendUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return process.env.REACT_APP_BACKEND_URL;
};

const api = axios.create({
  baseURL: getBackendUrl(),
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add authentication token
api.interceptors.request.use(
  async (config) => {
    try {
      // Get token from authContext or localStorage
      const token = authContext?.currentToken || localStorage.getItem(TOKEN_KEY);
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('auth_user');
      
      // Redirect to login if not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.reload();
      }
    }

    return Promise.reject(error);
  }
);

// API Methods
export const apiService = {
  // Health check
  health: () => api.get('/api/health'),

  // Bedtime Story endpoints (legacy naming for compatibility)
  bedtime: {
    generateStory: (data) => api.post('/api/bedtime-story/generate', data),
    getJobStatus: (jobId) => api.get(`/api/bedtime-story/job/${jobId}`),
    getVisuals: (storyId) => api.get(`/api/bedtime-story/${storyId}/visuals`),
  },

  // User endpoints
  user: {
    getDashboard: () => api.get('/api/user/dashboard'),
    getSubscription: () => api.get('/api/user/subscription'),
    getProfile: () => api.get('/api/user/profile'),
    updateProfile: (data) => api.put('/api/user/profile', data),
    getCredits: () => api.get('/api/user/credits'),
    getStories: (page = 1, limit = 30) => api.get('/api/user/stories', { params: { page, limit } }),
    submitFeedback: (data) => api.post('/api/user/feedback', data),
    submitSurvey: (data) => api.post('/api/user/survey-email', data),
  },

  // Payment endpoints
  payment: {
    createCheckout: (data) => api.post('/api/payments/whizz/checkout', data),
    createWhizzCheckout: (data) => api.post('/api/payments/whizz/checkout', data),
    getStatus: (sessionId) => api.get(`/api/payments/status/${sessionId}`),
    getHistory: () => api.get('/api/user/payment-history'),
    getPaymentHistory: () => api.get('/api/user/payment-history'),
    getPaymentMethods: () => api.get('/api/payments/methods'),
    removePaymentMethod: (methodId) => api.delete(`/api/payments/methods/${methodId}`),
    setDefaultPaymentMethod: (methodId) => api.post(`/api/payments/methods/${methodId}/default`),
    cancelSubscription: () => api.post('/api/payments/subscription/cancel'),
  },

  // Social/CartoonZ endpoints
  social: {
    getVideos: () => api.get('/api/social/videos'),
  },

  // Chat endpoint
  chat: (data) => api.post('/api/chat', data),

  // Z endpoints (Music, Games)
  z: {
    generateMusic: (data) => api.post('/api/z/music/generate', data),
  },

  // CartoonZ endpoints
  cartoonz: {
    transform: (data) => api.post('/api/cartoonz/transform', data),
    generateVideo: (data) => api.post('/api/cartoonz/generate-video', data),
  },
};

export default api;
